/// <amd-module name="Wit/MentionsAutocomplete/Providers/WorkItemMentionDataProvider" />
import { WorkItemResult, WorkItemSearchRequest, WorkItemSearchResponse } from "Wit/Clients/Search/Contracts/Search";
import { ISearchRestClient, getSearchClient } from "Wit/Clients/Search/RestClient/Search";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/MentionsAutocomplete/Resources";

import { getMultiTerms } from "Mention/Autocomplete/AutocompleteUtility";
import * as Telemetry from "../Utilities/Telemetry";
import { IWorkItemHighlighted, searchHighlightedWorkItem } from "../Utilities/WorkItemAutocompleteUtility";
import { IWorkItemProviderItem } from "./IWorkItemProviderItem";
import { IDictionaryNumberTo, Deferred } from "./Utils";
import { WorkItemProviderCache } from "./WorkItemProviderCache";

export type ISearchResultWorkItem = IWorkItemSearchResult<IWorkItemProviderItem, IWorkItemHighlighted>;

export interface IWorkItemProviderOptions {
    wiql?: string;
    workItemIdsToIgnore?: number[];
    useRestSearch?: boolean;
}

export interface IWorkItemSearchResult<TArtifact, TArtifactHighlighted> {
    original: TArtifact;
    highlighted: TArtifactHighlighted | null;
}

export class WorkItemMentionDataProvider {
    public static QUEUE_WAIT_MSEC = 10;
    public static MAX_BATCH_SIZE = 50;
    public static MULTIPLE_TERMS_LIMIT = 3;

    private static DEFAULT_WIQL: string = `([${CoreFieldRefNames.AuthorizedAs}] = @me
        AND [${CoreFieldRefNames.AuthorizedDate}] >= @today - 30)
    OR ([${CoreFieldRefNames.CreatedBy}] = @me
        AND [${CoreFieldRefNames.CreatedDate}] >= @today - 30)
    OR ([${CoreFieldRefNames.AssignedTo}] = @me
        AND [${CoreFieldRefNames.AuthorizedDate}] >= @today - 30)
    ORDER BY [${CoreFieldRefNames.AuthorizedDate}] DESC`;

    private _myWorkItems: Promise<IWorkItemProviderItem[]> | undefined;
    private _client: IWorkItemTrackingRestClient;
    private _searchClient: ISearchRestClient | null;
    private _wiql: string;
    private _workItemIdsToIgnore: IDictionaryNumberTo<boolean> = {};
    private _context: IVssPageContext;
    private _cache: WorkItemProviderCache;
    private _pageData: IPageData;
    private _useRestSearch: boolean;

    constructor(context: IVssPageContext, options?: IWorkItemProviderOptions) {
        this._context = context;

        this._wiql = options != null && options.wiql != null ? options.wiql : WorkItemMentionDataProvider.DEFAULT_WIQL;
        this._client = context.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");

        const pageService = context.getService<IVssPageService>("IVssPageService");
        this._pageData = pageService.getData()!;
        this._isFullTextEnabledAsync();

        this._useRestSearch = !!options?.useRestSearch;

        this._cache = new WorkItemProviderCache(
            context,
            this._client,
            WorkItemMentionDataProvider.QUEUE_WAIT_MSEC,
            WorkItemMentionDataProvider.MAX_BATCH_SIZE
        );

        if (options && options.workItemIdsToIgnore && options.workItemIdsToIgnore.length > 0) {
            for (const id of options.workItemIdsToIgnore) {
                this._workItemIdsToIgnore[id] = true;
            }
        }
    }

    public prefetch(): void {
        if (this._myWorkItems) {
            return;
        }

        const startTime = new Date().getTime();
        this._myWorkItems = this._getMyWorkItemsAsync();
        this._myWorkItems.then(_ => {
            const assignedEndTime = new Date().getTime();
            Telemetry.publishEvent(this._context, "WorkItemProvider.prefetch", {
                getMyAssignedWorkItemsDurationInMsec: (assignedEndTime - startTime).toString()
            });
        });
    }

    public getByIdAsync(id: string): Promise<IWorkItemProviderItem> {
        const workItemId = parseInt(id);
        const promises = this._getWorkItemPromises([workItemId]);

        if (promises.length === 1) {
            return promises[0];
        }

        throw new Error("Expected GetWorkItemsByIds to return 1 promise.");
    }

    public searchByIdAsync(id: number): Promise<ISearchResultWorkItem[]> {
        this.prefetch();

        const foundWorkItems: ISearchResultWorkItem[] = [];
        const foundWorkItemIds: IDictionaryNumberTo<boolean> = {};

        const addFoundWorkItem = (original: IWorkItemProviderItem, highlighted: IWorkItemHighlighted | null) => {
            if (!foundWorkItemIds[original.id]) {
                foundWorkItemIds[original.id] = true;
                foundWorkItems.push({ original, highlighted });
            }
        };

        const searchWorkItemByNumber = (workItem: IWorkItemProviderItem) => {
            if (workItem == null) {
                return;
            }
            const highlightedWorkItem = searchHighlightedWorkItem(id.toString(10), workItem);
            if (highlightedWorkItem) {
                addFoundWorkItem(workItem, highlightedWorkItem);
            }
        };

        const deferred = new Deferred<IWorkItemSearchResult<IWorkItemProviderItem, IWorkItemHighlighted>[]>();
        const exactMatchWorkItemPromise = this._getWorkItemsAsync([id]);
        Promise.all([exactMatchWorkItemPromise, this._myWorkItems!]).then(
            workItemLists => {
                workItemLists.forEach(workItemList => {
                    workItemList.forEach(workItem => {
                        searchWorkItemByNumber(workItem);
                    });
                });

                deferred.resolve(foundWorkItems);
            },
            function () {
                deferred.reject.apply(deferred, Array.from(arguments));
            }
        );

        return deferred.promise;
    }

    public searchAsync(term: string): Promise<ISearchResultWorkItem[]> {
        this.prefetch();

        const deferred = new Deferred<ISearchResultWorkItem[]>();
        this._myWorkItems!.then(
            workItems => {
                const filteredWorkItems: ISearchResultWorkItem[] = this._transformWorkItems(term, workItems, true);
                deferred.resolve(filteredWorkItems);
            },
            error => deferred.reject(error)
        );

        return deferred.promise;
    }

    public async searchOnServerAsync(term: string): Promise<ISearchResultWorkItem[]> {
        if (!term) {
            return [];
        }

        const resultCount: number = 50;
        const { terms, termsOverLimit } = WorkItemMentionDataProvider.getTerms(term);
        Telemetry.publishEvent(this._context, "WorkItemProvider.searchOnServerAsync", { termsOverLimit, numberOfTerms: terms.length });

        if (this.isRestSearchForMentionEnabled()) {
            const request: WorkItemSearchRequest = {
                $orderBy: null!,
                $skip: 0,
                $top: resultCount,
                filters: null!,
                includeFacets: false,
                searchText: term
            };
            const lower = (s: string) => s.toLowerCase();
            const result: WorkItemSearchResponse = await this._searchClient!.fetchWorkItemSearchResults(request);

            const wis = result.results.map((r: WorkItemResult) => {
                const projectName = r.project.name;
                const workItemType: string = r.fields[lower(CoreFieldRefNames.WorkItemType)];

                return {
                    id: +r.fields[lower(CoreFieldRefNames.Id)],
                    title: r.fields[lower(CoreFieldRefNames.Title)],
                    projectName,
                    workItemType
                } as IWorkItemProviderItem;
            });

            const filteredWorkItems: ISearchResultWorkItem[] = this._transformWorkItems(term, wis, false);
            return filteredWorkItems;
        } else {
            if (termsOverLimit) {
                throw new Error(Utils_String.localeFormat(Resources.AutocompleteTermsOverLimit, WorkItemMentionDataProvider.MULTIPLE_TERMS_LIMIT));
            } else {
                /**
                 * An example of composed "where" for term "1 2 3 text":
                 * WHERE
                 *     ([System.Title] CONTAINS WORDS '1' AND [System.Title] CONTAINS WORDS '2' AND [System.Title] CONTAINS WORDS '3' AND [System.Title] CONTAINS WORDS 'text')
                 *     OR [System.Title] CONTAINS WORDS '1 2 3 text'
                 *     OR [System.Id] = '1' OR [System.Id] = '2' OR [System.Id] = '3'
                 *     ORDER BY [System.AuthorizedDate] DESC
                 */

                // Title filter
                const fullTextEnabled = await this._isFullTextEnabledAsync();
                const operator = fullTextEnabled ? "CONTAINS WORDS" : "CONTAINS";
                const titleClauses = terms.map(item => `[${CoreFieldRefNames.Title}] ${operator} '${item}'`);
                let titleFilter = titleClauses.join(" AND ");
                if (terms.length > 1 && fullTextEnabled) {
                    titleFilter = `(${titleFilter}) OR [${CoreFieldRefNames.Title}] ${operator} '${term}'`;
                }

                // Id filter
                const idTerms = terms.filter(t => !isNaN(+t));
                const idClauses = idTerms.map(id => `OR [${CoreFieldRefNames.Id}] = '${id}'`);
                const idFilter = idClauses.join(" ");

                // ORDER BY option
                const orderBy = `ORDER BY [${CoreFieldRefNames.AuthorizedDate}] DESC`;

                // Final where
                const where = `${titleFilter}${idFilter ? ` ${idFilter}` : ""} ${orderBy}`;

                const workItems = await this._getWiqlResultsAsync(where, resultCount);
                const filteredWorkItems: ISearchResultWorkItem[] = this._transformWorkItems(term, workItems, false);
                return filteredWorkItems;
            }
        }
    }

    public static getTerms(term: string): { terms: string[]; termsOverLimit: boolean } {
        const terms: string[] = getMultiTerms(term, false);
        const termsOverLimit = terms.length > WorkItemMentionDataProvider.MULTIPLE_TERMS_LIMIT;

        return { terms, termsOverLimit };
    }

    private static isValidWorkItemId(id: number): boolean {
        return id >= 1 && id <= 2147483647;
    }

    private _isFullTextEnabledAsync: () => Promise<boolean> = async (): Promise<boolean> => {
        if (this._pageData.isHosted) {
            return true;
        }

        // On prem might disable full text
        const title = await this._client.getField(CoreFieldRefNames.Title);
        for (const { referenceName } of title.supportedOperations) {
            if (referenceName === "SupportedOperations.ContainsWords") {
                return true;
            }
        }

        return false;
    };

    private _getMyWorkItemsAsync(): Promise<IWorkItemProviderItem[]> {
        return this._getWiqlResultsAsync(this._wiql, 50);
    }

    private _getWorkItemsAsync(ids: number[]): Promise<IWorkItemProviderItem[]> {
        if (ids.length === 0) {
            throw new Error("ids must contain elements.");
        }

        return Promise.all(this._getWorkItemPromises(ids));
    }

    private _getWorkItemPromises(ids: number[]): Promise<IWorkItemProviderItem>[] {
        return ids.map(id => {
            if (WorkItemMentionDataProvider.isValidWorkItemId(id)) {
                return this._cache.cache.getArtifactPromise(id);
            }

            return Promise.resolve(null!);
        });
    }

    private async _getWiqlResultsAsync(whereClause: string, limit: number): Promise<IWorkItemProviderItem[]> {
        const query = `SELECT [${CoreFieldRefNames.Id}] FROM WorkItems WHERE ${whereClause}`;
        const wiqlResult = await this._client.queryByWiql({ query }, undefined, undefined, undefined, limit);
        const workItems: any[] = wiqlResult.workItems;
        const ids = workItems.map(({ id }: { id: number }) => id).filter(id => !this._workItemIdsToIgnore[id]);
        if (ids.length === 0) {
            return [];
        }

        return this._getWorkItemsAsync(ids);
    }

    public isRestSearchForMentionEnabled(): boolean {
        if (this._searchClient === undefined) {
            const restSearchAvailable = this._useRestSearch && this._isWorkItemSearchEnabled();

            // initialize the client here if everything is enabled, otherwise we'll get location service error if search service isn't enabled
            this._searchClient = restSearchAvailable ? getSearchClient(this._context) : null;
        }

        return !!this._searchClient;
    }

    private _isWorkItemSearchEnabled(): boolean {
        const service = this._context.getService<IVssContributionService>("IVssContributionService");
        try {
            const projectContribution = service.getContribution("ms.vss-work-web.workitem-project-search-provider");
            const collectionSearchProvider = service.getContribution("ms.vss-work-web.workitem-collection-search-provider");
            return !!(projectContribution || collectionSearchProvider);
        } catch (error) {
            Telemetry.traceError(this._context, "WorkItemProvider", error.message);
            return false;
        }
    }

    /**
     * Transforms work items into a search result model and optionally higlights matches
     * @param term The search term
     * @param workItems The result work items
     * @param requireHighlight Should we higlight work items and filter out ones that don't get highlighted
     */
    private _transformWorkItems(term: string, workItems: IWorkItemProviderItem[], requireHighlight: boolean): ISearchResultWorkItem[] {
        const filteredWorkItems: ISearchResultWorkItem[] = [];
        workItems.forEach(workItem => {
            if (!this._workItemIdsToIgnore[workItem.id]) {
                const highlighted = requireHighlight ? searchHighlightedWorkItem(term, workItem) : null;

                if (highlighted || !requireHighlight) {
                    filteredWorkItems.push({ original: workItem, highlighted });
                }
            }
        });

        return filteredWorkItems;
    }
}
